import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * WebSocket hook for real-time notifications
 * 
 * @param {string} token - JWT authentication token
 * @param {function} onNotification - Callback when a new notification arrives
 * @param {function} onUnreadCountUpdate - Callback when unread count changes
 * @returns {object} - { isConnected, unreadCount, sendMessage, reconnect }
 */
export function useNotificationWebSocket(token, onNotification, onUnreadCountUpdate) {
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // Start with 1 second

  // Get WebSocket URL
  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert HTTP(S) to WS(S)
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const baseUrl = backendUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}://${baseUrl}/api/ws/notifications?token=${token}`;
  }, [token]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!token) {
      console.log('No token provided, skipping WebSocket connection');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    const wsUrl = getWsUrl();
    console.log('Connecting to WebSocket:', wsUrl.replace(/token=.*/, 'token=***'));

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data.type);

          switch (data.type) {
            case 'notification':
              // New notification received
              if (onNotification) {
                onNotification(data.payload);
              }
              break;

            case 'unread_count':
              // Unread count updated
              setUnreadCount(data.payload.unread_count);
              if (onUnreadCountUpdate) {
                onUnreadCountUpdate(data.payload.unread_count);
              }
              break;

            case 'ping':
              // Respond to keepalive ping
              ws.send(JSON.stringify({ type: 'pong' }));
              break;

            case 'pong':
              // Keepalive response received
              break;

            default:
              console.log('Unknown WebSocket message type:', data.type);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt reconnection with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else {
          console.log('Max reconnection attempts reached');
        }
      };
    } catch (err) {
      console.error('Error creating WebSocket:', err);
      setIsConnected(false);
    }
  }, [token, getWsUrl, onNotification, onUnreadCountUpdate]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // Manual reconnect
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    disconnect();
    setTimeout(connect, 100);
  }, [connect, disconnect]);

  // Send message via WebSocket
  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }, []);

  // Mark notification as read via WebSocket
  const markAsRead = useCallback((notificationId) => {
    sendMessage({
      type: 'mark_read',
      notification_id: notificationId
    });
  }, [sendMessage]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Keepalive ping every 25 seconds
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(() => {
      sendMessage({ type: 'ping' });
    }, 25000);

    return () => clearInterval(pingInterval);
  }, [isConnected, sendMessage]);

  return {
    isConnected,
    unreadCount,
    sendMessage,
    markAsRead,
    reconnect,
    disconnect
  };
}

export default useNotificationWebSocket;
